import React, { useState } from "react";
import { MapPin, Clock, Mail, Phone, Send, MessageSquare, CheckCircle2 } from "lucide-react";
import { company } from "../mock";
import Reveal from "../components/Reveal";
import { useToast } from "../hooks/use-toast";

const choices = ["Upit", "Zahtjev za ponudu", "Primjedba / Sugestija"];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    topic: "Upit",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    // Save locally as this is frontend-only
    const store = JSON.parse(localStorage.getItem("elorte_messages") || "[]");
    store.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("elorte_messages", JSON.stringify(store));
    setSent(true);
    toast({
      title: "Poruka poslana",
      description: "Hvala Vam! Javljamo Vam se u što kraćem roku.",
    });
    setForm({ name: "", phone: "", email: "", topic: "Upit", message: "" });
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-neutral-950 text-white overflow-hidden noise">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute -bottom-32 -left-16 w-[520px] h-[520px] rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-amber-300">
              <MessageSquare className="w-3.5 h-3.5" /> Imate pitanje?
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display mt-5 text-5xl md:text-7xl font-bold leading-tight max-w-3xl">
              Kontaktirajte <span className="text-amber-400">nas</span>.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-5 text-lg text-neutral-300 max-w-2xl">
              A mi ćemo Vam se javiti u što kraćem roku. Stojimo Vam na raspolaganju za sve upite vezane za naše proizvode.
            </p>
          </Reveal>
        </div>
      </section>

      {/* INFO + FORM */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10">
          {/* Info cards */}
          <div className="lg:col-span-5 space-y-4">
            {[
              { icon: MapPin, title: "Adresa", value: "Rosulje 77, 74264 Jelah, BiH" },
              { icon: Clock, title: "Radno vrijeme", value: "Ponedjeljak – Petak, 08:00 – 16:00" },
              { icon: Mail, title: "Email", value: "info@elorte-medaric.ba\ncontact@elorte-medaric.ba" },
              { icon: Phone, title: "Telefon", value: company.phone },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="group card-hover bg-neutral-50 border border-neutral-200 rounded-2xl p-6 flex gap-5 hover:border-amber-400 hover:bg-white hover:shadow-lg">
                  <div className="w-12 h-12 rounded-xl bg-neutral-950 group-hover:bg-amber-400 flex items-center justify-center transition-colors shrink-0">
                    <c.icon className="w-6 h-6 text-amber-400 group-hover:text-neutral-950 transition-colors" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-neutral-500">{c.title}</div>
                    <div className="mt-1 font-semibold text-neutral-900 whitespace-pre-line">{c.value}</div>
                  </div>
                </div>
              </Reveal>
            ))}

            {/* Map */}
            <Reveal delay={400}>
              <div className="rounded-2xl overflow-hidden border border-neutral-200 h-64">
                <iframe
                  title="Elorte-Medarić Jelah"
                  src="https://www.google.com/maps?q=Rosulje%2077%2C%20Jelah%2C%20Bosnia&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-neutral-950 text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
                <div className="relative">
                  <h2 className="font-display text-3xl md:text-4xl font-bold">Kontaktiraj nas</h2>
                  <p className="mt-2 text-neutral-400">Popunite formular i pišite nam – stojimo Vam na raspolaganju.</p>

                  {sent && (
                    <div className="mt-6 flex items-center gap-3 bg-amber-400/10 border border-amber-400/30 rounded-xl p-4 text-amber-200">
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Poruka je uspješno poslana. Hvala Vam!</span>
                    </div>
                  )}

                  <form onSubmit={onSubmit} className="mt-8 grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-1">
                      <label className="text-xs uppercase tracking-widest text-neutral-400">Ime i prezime *</label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="mt-2 w-full bg-white/5 border border-white/10 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 outline-none rounded-xl px-4 py-3 text-white placeholder-neutral-500 transition-colors"
                        placeholder="Vaše ime"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <label className="text-xs uppercase tracking-widest text-neutral-400">Broj telefona *</label>
                      <input
                        required
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="mt-2 w-full bg-white/5 border border-white/10 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 outline-none rounded-xl px-4 py-3 text-white placeholder-neutral-500 transition-colors"
                        placeholder="+387 ..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest text-neutral-400">Email adresa *</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="mt-2 w-full bg-white/5 border border-white/10 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 outline-none rounded-xl px-4 py-3 text-white placeholder-neutral-500 transition-colors"
                        placeholder="email@primjer.com"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest text-neutral-400">Tip poruke</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {choices.map((c) => (
                          <button
                            type="button"
                            key={c}
                            onClick={() => setForm({ ...form, topic: c })}
                            className={`btn-primary px-4 py-2 rounded-full text-sm font-medium border ${
                              form.topic === c
                                ? "bg-amber-400 text-neutral-950 border-amber-400"
                                : "bg-white/5 text-neutral-300 border-white/10 hover:border-amber-400"
                            }`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest text-neutral-400">Poruka *</label>
                      <textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="mt-2 w-full bg-white/5 border border-white/10 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 outline-none rounded-xl px-4 py-3 text-white placeholder-neutral-500 transition-colors resize-none"
                        placeholder="Napišite Vašu poruku..."
                      />
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
                      <p className="text-xs text-neutral-500 max-w-md">
                        Slanjem poruke prihvatate da Vas kontaktiramo putem ostavljenih podataka.
                      </p>
                      <button
                        type="submit"
                        className="btn-primary inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-neutral-950 font-semibold px-7 py-3.5 rounded-full"
                      >
                        Pošalji poruku <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
